import { useState } from "react";
import { toast } from "sonner";
import { MEMBERSHIP_IMAGE } from "@/data/content";
import { joinHouseList } from "@/lib/api";
import { useFormattedPhone } from "@/lib/usePhone";
import SmsConsent from "@/components/SmsConsent";

const STEWARDSHIP_INCLUDES = [
  {
    title: "Permanent Steward Number",
    body:
      "Your Steward Number is reserved exclusively for you and will never be reassigned. It will be engraved on your brass Founder's Card and remain yours for life.",
  },
  {
    title: "The Founder's Brass Card",
    body:
      "A numbered solid brass card crafted exclusively for Founding Stewards. Not a rewards card. A keepsake.",
  },
  {
    title: "Lifetime Founder's Privilege",
    body:
      "Receive 48-hour priority access to every House Drop before it becomes available to the public. Whenever a Limited Drop is released, Founding Stewards are invited first.",
  },
  {
    title: "The Vault",
    body:
      "The Vault is where The House keeps its secrets. Reserved exclusively for Founding Stewards, The Vault grants access to secret menu creations, off-menu drinks, retired favorites, experimental recipes, surprise releases, and other experiences that never appear on the public menu. Not everything belongs on the menu.",
  },
  {
    title: "Annual Founder's Experience",
    body:
      "Each year, Founding Stewards will be invited to a private gathering featuring exclusive tastings, previews, and experiences created only for them.",
  },
  {
    title: "Your Place in The House",
    body:
      "With permission, each Founding Steward's name or Steward Number will become a permanent part of The House through the Founding Stewards display.",
  },
  {
    title: "House Alerts: Founder's Priority",
    body:
      "Receive announcements before the public whenever new House Drops launch, limited releases become available, pop-ups are announced, or new experiences are introduced.",
  },
  {
    title: "Obsession Alerts",
    body:
      "Be the first to know when an item from your Obsession List returns, when a new Weekly Drop matches your interests, when Reserve-only releases become available, and when seasonal favorites return.",
    tag: "Coming Soon",
  },
  {
    title: "The House Chronicles",
    body:
      "The story of The House is still being written. Receive exclusive entries from The House Chronicles, documenting the evolution of The Uncanny House through Pumabilt, future House Drops, new locations, milestone moments, and the ideas taking shape before they're introduced to the world. Available exclusively to Founding Stewards.",
    tag: "Exclusive",
    span: true,
  },
];

export default function Membership() {
  const [form, setForm] = useState({ name: "", email: "", city: "", sms_opt_in: false });
  const phone = useFormattedPhone();
  const [submitting, setSubmitting] = useState(false);
  const [member, setMember] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const phoneEntered = phone.hasValue;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    if (phoneEntered && !phone.isValid) {
      toast.error(phone.error);
      return;
    }
    if (phoneEntered && !form.sms_opt_in) {
      toast.error("Please check the SMS consent box to receive texts.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await joinHouseList({
        name: form.name,
        email: form.email,
        city: form.city || undefined,
        phone: phone.normalized || undefined,
        sms_opt_in: !!form.sms_opt_in,
      });
      setMember(data);
      toast.success("Your Stewardship claim has been received. The House will be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went sideways. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="stewardship-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      {/* Header */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="label-eyebrow text-lavender">Founding Stewardship</p>
        <h1
          data-testid="stewardship-title"
          className="font-display text-bone text-5xl md:text-7xl lg:text-[92px] mt-6 leading-[0.95]"
        >
          Claim Your <em className="not-italic text-lavender">Steward Number</em>
        </h1>
        <p className="mt-8 font-display italic text-bone/85 text-2xl md:text-3xl leading-snug max-w-3xl">
          111 Founding House Stewards. One Opportunity. Never Offered Again.
        </p>

        <div className="mt-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8 space-y-6 text-bone/75 leading-relaxed max-w-2xl">
            <p>
              Before The House officially opens its doors, we are inviting 111 Founding Stewards to help
              lay its foundation.
            </p>
            <p>
              A Founding Stewardship is a one-time contribution of{" "}
              <span className="text-bone font-display">$1,000</span>. It is not an investment or ownership
              interest in The Uncanny House. It is an invitation to become part of its story from the very
              beginning.
            </p>
            <p className="font-display italic text-lavender text-lg md:text-xl">
              Once all 111 Stewardships have been claimed, Founding Stewardship will close permanently.
            </p>
          </div>
          <aside className="md:col-span-4 border-l-2 border-lavender/40 pl-6 md:ml-auto">
            <p className="label-eyebrow text-bone/50">Contribution</p>
            <p className="font-display text-bone text-6xl mt-3 tabular-nums">$1,000</p>
            <p className="text-bone/60 text-sm mt-3">One-time · Non-refundable</p>
            <p className="text-bone/50 text-xs mt-2">Not an investment. Not equity.</p>
          </aside>
        </div>
      </section>

      {/* THE CARD + FORM */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-20 md:mt-28">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          {/* The Card */}
          <div className="md:col-span-6">
            <p className="label-eyebrow">The Card</p>

            <figure
              data-testid="steward-card"
              className="mt-6 relative w-full overflow-hidden border border-brass/25 bg-obsidian"
            >
              <img
                src={MEMBERSHIP_IMAGE}
                alt="Founding Steward card — front and back, with envelope"
                className="block w-full h-auto"
              />
            </figure>

            <figcaption className="mt-5 flex items-baseline justify-between gap-4 text-bone/65 text-sm">
              <span className="font-display italic text-bone/80 text-base">
                {member ? `Yours: ${member.member_number}` : "Founding Steward"}
              </span>
              <span className="label-eyebrow text-bone/45">N° 001 — 111</span>
            </figcaption>
          </div>

          {/* The Form */}
          <div className="md:col-span-6">
            <p className="label-eyebrow text-lavender">Claim Your Number</p>
            <h2 className="font-display text-bone text-3xl md:text-4xl mt-4 leading-tight">
              Reserve your place among the Founding Stewards.
            </h2>

            {member ? (
              <div data-testid="stewardship-success" className="mt-8 border border-lavender/40 bg-espresso/20 p-7">
                <p className="label-eyebrow text-lavender">Confirmed</p>
                <p className="font-display text-bone text-3xl mt-3">Welcome, {member.name}.</p>
                <p className="text-bone/70 mt-3">
                  Your permanent Steward Number is{" "}
                  <span className="text-lavender">{member.member_number}</span>.
                  We&apos;ll be in touch with your Founder&apos;s Card details shortly.
                </p>
              </div>
            ) : (
              <form data-testid="stewardship-form" onSubmit={onSubmit} className="mt-8 space-y-5">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Name</label>
                  <input
                    data-testid="stewardship-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-lavender outline-none py-3 text-bone placeholder-bone/30 font-display text-xl transition-colors"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Email</label>
                  <input
                    data-testid="stewardship-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-lavender outline-none py-3 text-bone placeholder-bone/30 font-display text-xl transition-colors"
                    placeholder="you@somewhere.house"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">
                    City <span className="text-bone/35">(optional)</span>
                  </label>
                  <input
                    data-testid="stewardship-input-city"
                    name="city"
                    value={form.city}
                    onChange={onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-lavender outline-none py-3 text-bone placeholder-bone/30 font-display text-xl transition-colors"
                    placeholder="Fresno, Clovis, Madera…"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">
                    Mobile Number <span className="text-bone/35">(optional)</span>
                  </label>
                  <input
                    data-testid="stewardship-input-phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={phone.display}
                    onChange={phone.onChange}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-lavender outline-none py-3 text-bone placeholder-bone/30 font-display text-xl transition-colors"
                    placeholder="1 (555) 555-5555"
                  />
                  {phoneEntered && !phone.isValid && (
                    <p className="mt-2 text-[11px] text-red-300/85">{phone.error}</p>
                  )}
                </div>

                <SmsConsent
                  testId="stewardship-sms-consent"
                  checked={form.sms_opt_in}
                  onChange={(v) => setForm((f) => ({ ...f, sms_opt_in: v }))}
                  required={phoneEntered}
                />

                <button
                  type="submit"
                  data-testid="membership-submit"
                  disabled={submitting}
                  className="mt-4 w-full md:w-auto inline-flex items-center justify-center px-8 py-4 bg-lavender text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-bone transition-colors disabled:opacity-60"
                >
                  {submitting ? "Claiming…" : "Claim My Steward Number"}
                </button>

                <p className="text-bone/40 text-xs mt-4">
                  Steward Numbers are issued in order received. Your details are kept in the strictest confidence.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* YOUR STEWARDSHIP INCLUDES */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-24 md:mt-32">
        <div className="flex items-baseline justify-between gap-6 border-b border-lavender/20 pb-6">
          <div>
            <p className="label-eyebrow text-lavender">The Privileges</p>
            <h2 className="font-display text-bone text-4xl md:text-5xl mt-3 leading-[1.05]">
              Your Stewardship Includes
            </h2>
          </div>
          <p className="hidden md:block font-display italic text-bone/50 text-lg">Nine curated privileges.</p>
        </div>

        <ol className="mt-12 grid md:grid-cols-2 gap-6" data-testid="stewardship-includes">
          {STEWARDSHIP_INCLUDES.map((perk, i) => (
            <li
              key={perk.title}
              data-testid={`stewardship-perk-${i + 1}`}
              className={`relative border border-bone/10 p-8 hover:border-lavender/50 transition-colors duration-300 group ${
                perk.span ? "md:col-span-2 md:max-w-2xl md:mx-auto md:w-full" : ""
              }`}
            >
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-display italic text-lavender/70 text-2xl tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {perk.tag && (
                  <span className="text-[9px] uppercase tracking-[0.3em] text-lavender border border-lavender/40 px-2 py-1">
                    {perk.tag}
                  </span>
                )}
              </div>
              <p className="font-display text-bone text-xl md:text-2xl mt-5 leading-snug group-hover:text-lavender transition-colors">
                {perk.title}
              </p>
              <p className="mt-4 text-bone/70 text-[15px] leading-relaxed">{perk.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* STEWARDSHIP MATTERS */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-24 md:mt-32">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="label-eyebrow text-lavender">Why It Matters</p>
            <h2 className="font-display text-bone text-4xl md:text-5xl mt-3 leading-[1.05]">
              Stewardship Matters
            </h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-bone/80 leading-relaxed text-lg max-w-2xl">
            <p>
              The Uncanny House is being built one thoughtful detail at a time. Every Founding Steward
              becomes part of that story.
            </p>
            <p>
              Your Stewardship helps bring The House to life while securing your permanent place in its
              history.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT STEWARDSHIP IS NOT */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-16 md:mt-24">
        <div className="border-l-2 border-lavender/40 pl-6 md:pl-10 max-w-4xl">
          <p className="label-eyebrow text-bone/60">Full Transparency</p>
          <h2 className="font-display text-bone text-3xl md:text-4xl mt-3 leading-[1.1]">
            What Stewardship Is Not
          </h2>
          <p className="mt-6 text-bone/70 leading-relaxed text-[15px] md:text-base">
            Founding Stewardship does not provide ownership, equity, voting rights, or a financial return.
          </p>
          <p className="mt-4 text-bone/70 leading-relaxed text-[15px] md:text-base">
            It is a one-time contribution that grants the recognition, privileges, and experiences described
            above while supporting the creation and growth of The Uncanny House.
          </p>
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-32 md:mt-48 mb-16 text-center">
        <p
          data-testid="stewardship-closing"
          className="font-display italic text-bone text-3xl md:text-5xl leading-[1.15] max-w-3xl mx-auto"
        >
          The Uncanny House will be remembered.
        </p>
        <p className="mt-6 font-display italic text-lavender text-3xl md:text-5xl leading-[1.15] max-w-3xl mx-auto">
          The Founding Stewards will be remembered with it.
        </p>
      </section>
    </div>
  );
}
